import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { conDosDecimales } from '../venta/PiezasDeVenta';

interface Cuenta {
  readonly id: string;
  readonly nombre: string;
  readonly banco: string;
  readonly numero: string;
  readonly saldo: string;
}

interface Valor {
  readonly id: string;
  readonly numero: string;
  readonly banco: string;
  readonly fechaDeCobro: string;
  readonly importe: string;
  readonly nombreDeQuienLoDio: string | null;
}

interface Conciliacion {
  readonly lineas: readonly {
    readonly id: string;
    readonly fecha: string;
    readonly descripcion: string;
    readonly importe: string;
  }[];
  readonly movimientos: readonly {
    readonly id: string;
    readonly fecha: string;
    readonly concepto: string;
    readonly importe: string;
  }[];
  readonly propuestas: readonly { readonly lineaId: string; readonly movimientoId: string }[];
}

interface Deposito {
  readonly id: string;
  readonly fecha: string;
  readonly total: string;
  readonly acreditadoEn: string | null;
  readonly cuenta: string;
}

/**
 * Bancos y cartera de cheques.
 *
 * Lo que esta pantalla tiene que dejar claro es **dónde está la plata**, y por eso muestra las tres
 * cosas juntas: lo que hay en el banco, lo que hay en la cartera esperando depositarse, y lo que
 * está en el medio —depositado y todavía sin acreditar—.
 *
 * Esa tercera columna es la que no existía y la que motivó la entrega: sin ella, un comercio que
 * llevó cinco cheques al banco el viernes no tiene forma de saber si la plata ya llegó.
 */
export function Bancos(): React.ReactElement {
  const { t } = useTranslation();
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();
  const [elegidos, setElegidos] = useState<string[]>([]);
  /** Qué cuenta se está conciliando. Sin elegir, la sección ni se pide al servidor. */
  const [conciliando, setConciliando] = useState<string | null>(null);
  const [nueva, setNueva] = useState({ nombre: '', banco: '', numero: '' });

  const cuentas = useQuery({
    queryKey: ['cuentas-bancarias'],
    queryFn: () => pedir<Cuenta[]>('/cuentas-bancarias'),
  });
  const cartera = useQuery({ queryKey: ['valores'], queryFn: () => pedir<Valor[]>('/valores') });
  const depositos = useQuery({
    queryKey: ['depositos-bancarios'],
    queryFn: () => pedir<Deposito[]>('/depositos-bancarios'),
  });

  const conciliacion = useQuery({
    queryKey: ['conciliacion', conciliando],
    enabled: conciliando !== null,
    queryFn: () => pedir<Conciliacion>(`/cuentas-bancarias/${conciliando ?? ''}/conciliacion`),
  });

  function refrescar(): void {
    for (const clave of ['cuentas-bancarias', 'valores', 'depositos-bancarios', 'conciliacion']) {
      void cola.invalidateQueries({ queryKey: [clave] });
    }
  }

  const crearCuenta = useMutation({
    mutationFn: () =>
      pedir<{ id: string }>('/cuentas-bancarias', { method: 'POST', cuerpo: nueva }),
    onSuccess: () => {
      setNueva({ nombre: '', banco: '', numero: '' });
      refrescar();
    },
  });

  const subirExtracto = useMutation({
    mutationFn: async (archivo: File) =>
      pedir<{ cargadas: number; rechazadas: readonly unknown[] }>(
        `/cuentas-bancarias/${conciliando ?? ''}/extracto`,
        { method: 'POST', cuerpo: { contenido: await archivo.text() } },
      ),
    onSuccess: refrescar,
  });

  const conciliar = useMutation({
    mutationFn: (par: { movimientoId: string; lineaDeExtractoId: string }) =>
      pedir<{ conciliado: boolean }>('/conciliaciones', { method: 'POST', cuerpo: par }),
    onSuccess: refrescar,
  });

  const depositar = useMutation({
    mutationFn: (cuentaBancariaId: string) =>
      pedir<{ total: string }>('/depositos-bancarios', {
        method: 'POST',
        cuerpo: { cuentaBancariaId, valores: elegidos },
      }),
    onSuccess: () => {
      setElegidos([]);
      refrescar();
    },
  });

  const acreditar = useMutation({
    mutationFn: (depositoId: string) =>
      pedir<unknown>(`/depositos-bancarios/${depositoId}/acreditacion`, {
        method: 'POST',
        cuerpo: {},
      }),
    onSuccess: refrescar,
  });

  if (cuentas.isPending) return <Cargando />;
  if (cuentas.isError) return <Problema error={cuentas.error} />;

  const enCartera = (cartera.data ?? []).reduce((suma, uno) => suma + Number(uno.importe), 0);
  const enTransito = (depositos.data ?? [])
    .filter((uno) => uno.acreditadoEn === null)
    .reduce((suma, uno) => suma + Number(uno.total), 0);

  const columnasDeCartera: Columna<Valor>[] = [
    {
      clave: 'elegir',
      titulo: '',
      contenido: (fila) => (
        <input
          type="checkbox"
          aria-label={`${t('bancos.cheque')} ${fila.numero}`}
          checked={elegidos.includes(fila.id)}
          onChange={() => {
            setElegidos(
              elegidos.includes(fila.id)
                ? elegidos.filter((uno) => uno !== fila.id)
                : [...elegidos, fila.id],
            );
          }}
        />
      ),
    },
    { clave: 'numero', titulo: t('bancos.numero'), contenido: (fila) => fila.numero },
    { clave: 'banco', titulo: t('bancos.banco'), contenido: (fila) => fila.banco },
    {
      clave: 'quien',
      titulo: t('bancos.deQuien'),
      contenido: (fila) => fila.nombreDeQuienLoDio,
    },
    { clave: 'fecha', titulo: t('bancos.fechaDeCobro'), contenido: (fila) => fila.fechaDeCobro },
    {
      clave: 'importe',
      titulo: t('bancos.importe'),
      numerica: true,
      contenido: (fila) => conDosDecimales(fila.importe),
    },
  ];

  const columnasDeDepositos: Columna<Deposito>[] = [
    { clave: 'fecha', titulo: t('bancos.fecha'), contenido: (fila) => fila.fecha },
    { clave: 'cuenta', titulo: t('bancos.cuenta'), contenido: (fila) => fila.cuenta },
    {
      clave: 'total',
      titulo: t('bancos.importe'),
      numerica: true,
      contenido: (fila) => conDosDecimales(fila.total),
    },
    {
      clave: 'estado',
      titulo: t('bancos.estado'),
      contenido: (fila) =>
        fila.acreditadoEn === null ? (
          <Boton
            cargando={acreditar.isPending}
            onClick={() => {
              acreditar.mutate(fila.id);
            }}
          >
            {t('bancos.acreditar')}
          </Boton>
        ) : (
          <Insignia tono="bien">{t('bancos.acreditadoEl', { fecha: fila.acreditadoEn })}</Insignia>
        ),
    },
  ];

  return (
    <section className="flex flex-col gap-4">
      <Encabezado titulo={t('bancos.titulo')} descripcion={t('bancos.descripcion')} />

      {/* Dónde está la plata, en tres números. El del medio es el que no existía: sin él, un
          comercio que llevó cinco cheques al banco el viernes no sabe si ya llegaron. */}
      <Tarjeta>
        <div className="flex flex-wrap gap-6 text-sm">
          <div>
            <p className="text-xs text-texto-tenue">{t('bancos.enElBanco')}</p>
            <p className="numero text-lg">
              {conDosDecimales(
                String((cuentas.data ?? []).reduce((suma, uno) => suma + Number(uno.saldo), 0)),
              )}
            </p>
          </div>
          <div>
            <p className="text-xs text-texto-tenue">{t('bancos.enTransito')}</p>
            <p className="numero text-lg">{conDosDecimales(String(enTransito))}</p>
          </div>
          <div>
            <p className="text-xs text-texto-tenue">{t('bancos.enCartera')}</p>
            <p className="numero text-lg">{conDosDecimales(String(enCartera))}</p>
          </div>
        </div>
      </Tarjeta>

      <Tarjeta>
        <h2 className="mb-2 text-sm font-semibold">{t('bancos.cuentas')}</h2>
        {/*
          Declarar la cuenta es lo primero que hace falta y no había forma de hacerlo: el módulo
          entero —depositar, acreditar, conciliar— empieza acá, y sin este formulario la pantalla
          era una lista vacía sin salida.
        */}
        <div className="mb-3 flex flex-wrap items-end gap-2">
          {(['nombre', 'banco', 'numero'] as const).map((campo) => (
            <label key={campo} className="flex flex-col gap-1 text-xs text-texto-tenue">
              {t(`bancos.${campo}`)}
              <input
                value={nueva[campo]}
                onChange={(evento) => {
                  setNueva({ ...nueva, [campo]: evento.target.value });
                }}
                className="rounded-marco border border-borde bg-superficie px-2 py-1 text-sm text-texto"
              />
            </label>
          ))}
          <Boton
            tono="principal"
            cargando={crearCuenta.isPending}
            disabled={nueva.nombre.trim() === '' || nueva.banco.trim() === ''}
            onClick={() => {
              crearCuenta.mutate();
            }}
          >
            {t('bancos.agregarCuenta')}
          </Boton>
        </div>
        {crearCuenta.isError ? (
          <p className="mb-2 text-xs text-mal">{textoDeError(crearCuenta.error)}</p>
        ) : null}
        {cuentas.data.length === 0 ? (
          <Vacio titulo={t('bancos.sinCuentas')} />
        ) : (
          <Tabla
            filas={cuentas.data}
            claveDeFila={(fila) => fila.id}
            columnas={[
              { clave: 'nombre', titulo: t('bancos.cuenta'), contenido: (fila) => fila.nombre },
              { clave: 'banco', titulo: t('bancos.banco'), contenido: (fila) => fila.banco },
              { clave: 'numero', titulo: t('bancos.numero'), contenido: (fila) => fila.numero },
              {
                clave: 'saldo',
                titulo: t('bancos.saldo'),
                numerica: true,
                contenido: (fila) => conDosDecimales(fila.saldo),
              },
            ]}
          />
        )}
      </Tarjeta>

      <Tarjeta>
        <h2 className="mb-2 text-sm font-semibold">{t('bancos.cartera')}</h2>
        {(cartera.data ?? []).length === 0 ? (
          <Vacio titulo={t('bancos.sinValores')} />
        ) : (
          <>
            <Tabla
              filas={cartera.data ?? []}
              columnas={columnasDeCartera}
              claveDeFila={(fila) => fila.id}
            />
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {(cuentas.data ?? []).map((cuenta) => (
                <Boton
                  key={cuenta.id}
                  tono="principal"
                  disabled={elegidos.length === 0}
                  cargando={depositar.isPending}
                  onClick={() => {
                    depositar.mutate(cuenta.id);
                  }}
                >
                  {t('bancos.depositarEn', { total: elegidos.length, cuenta: cuenta.nombre })}
                </Boton>
              ))}
              <span className="text-xs text-texto-tenue">{t('bancos.depositarAyuda')}</span>
            </div>
            {depositar.isError ? (
              <p className="mt-2 text-xs text-mal">{textoDeError(depositar.error)}</p>
            ) : null}
          </>
        )}
      </Tarjeta>

      <Tarjeta>
        <h2 className="mb-2 text-sm font-semibold">{t('bancos.depositos')}</h2>
        {acreditar.isError ? (
          <p className="mb-2 text-xs text-mal">{textoDeError(acreditar.error)}</p>
        ) : null}
        {(depositos.data ?? []).length === 0 ? (
          <Vacio titulo={t('bancos.sinDepositos')} />
        ) : (
          <Tabla
            filas={depositos.data ?? []}
            columnas={columnasDeDepositos}
            claveDeFila={(fila) => fila.id}
          />
        )}
      </Tarjeta>

      {/*
        Conciliar. Lo que la pantalla tiene que dejar ver **no es lo que se casó, es lo que no**:
        la línea del banco que el sistema no explica y el movimiento del sistema que el banco no
        confirma. Por eso lo conciliado desaparece de las dos listas en vez de quedar tildado.
      */}
      <Tarjeta>
        <h2 className="mb-2 text-sm font-semibold">{t('bancos.conciliacion')}</h2>
        {(cuentas.data ?? []).length === 0 ? (
          <Vacio titulo={t('bancos.sinCuentas')} />
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1 text-xs text-texto-tenue">
                {t('bancos.cuenta')}
                <select
                  value={conciliando ?? ''}
                  onChange={(evento) => {
                    setConciliando(evento.target.value === '' ? null : evento.target.value);
                  }}
                  className="rounded-marco border border-borde bg-superficie px-2 py-1 text-sm text-texto"
                >
                  <option value="">{t('bancos.elegiCuenta')}</option>
                  {(cuentas.data ?? []).map((cuenta) => (
                    <option key={cuenta.id} value={cuenta.id}>
                      {cuenta.nombre}
                    </option>
                  ))}
                </select>
              </label>
              {conciliando === null ? null : (
                <label className="flex flex-col gap-1 text-xs text-texto-tenue">
                  {t('bancos.subirExtracto')}
                  <input
                    type="file"
                    accept=".csv,text/csv,text/plain"
                    className="text-sm text-texto"
                    onChange={(evento) => {
                      const archivo = evento.target.files?.[0];
                      if (archivo !== undefined) subirExtracto.mutate(archivo);
                      evento.target.value = '';
                    }}
                  />
                </label>
              )}
            </div>

            {subirExtracto.isError ? (
              <p className="mb-2 text-xs text-mal">{textoDeError(subirExtracto.error)}</p>
            ) : null}
            {subirExtracto.isSuccess ? (
              <p className="mb-2 text-xs text-bien">
                {t('bancos.extractoCargado', {
                  cargadas: subirExtracto.data.cargadas,
                  rechazadas: subirExtracto.data.rechazadas.length,
                })}
              </p>
            ) : null}
            {conciliar.isError ? (
              <p className="mb-2 text-xs text-mal">{textoDeError(conciliar.error)}</p>
            ) : null}

            {conciliando === null ? (
              <p className="text-xs text-texto-tenue">{t('bancos.elegiCuenta')}</p>
            ) : null}
            {conciliacion.isSuccess ? (
              <div className="grid gap-4 lg:grid-cols-2">
                <div>
                  <p className="mb-1 text-xs font-medium text-texto-tenue">
                    {t('bancos.diceElBanco')}
                  </p>
                  {conciliacion.data.lineas.length === 0 ? (
                    <p className="text-xs text-texto-tenue">{t('bancos.todoExplicado')}</p>
                  ) : (
                    <ul className="flex flex-col divide-y divide-borde">
                      {conciliacion.data.lineas.map((linea) => {
                        const propuesta = conciliacion.data.propuestas.find(
                          (una) => una.lineaId === linea.id,
                        );
                        return (
                          <li key={linea.id} className="flex items-center gap-2 py-2 text-sm">
                            <span className="min-w-0 flex-1">
                              {linea.descripcion}
                              <span className="block text-xs text-texto-tenue">{linea.fecha}</span>
                            </span>
                            <span className="numero">{conDosDecimales(linea.importe)}</span>
                            {propuesta === undefined ? (
                              <span className="text-xs text-texto-tenue">
                                {t('bancos.sinCandidato')}
                              </span>
                            ) : (
                              <Boton
                                tono="principal"
                                cargando={conciliar.isPending}
                                onClick={() => {
                                  conciliar.mutate({
                                    movimientoId: propuesta.movimientoId,
                                    lineaDeExtractoId: linea.id,
                                  });
                                }}
                              >
                                {t('bancos.conciliar')}
                              </Boton>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                <div>
                  <p className="mb-1 text-xs font-medium text-texto-tenue">
                    {t('bancos.diceElSistema')}
                  </p>
                  {conciliacion.data.movimientos.length === 0 ? (
                    <p className="text-xs text-texto-tenue">{t('bancos.todoConfirmado')}</p>
                  ) : (
                    <ul className="flex flex-col divide-y divide-borde">
                      {conciliacion.data.movimientos.map((movimiento) => (
                        <li key={movimiento.id} className="flex items-center gap-2 py-2 text-sm">
                          <span className="min-w-0 flex-1">
                            {movimiento.concepto}
                            <span className="block text-xs text-texto-tenue">
                              {movimiento.fecha}
                            </span>
                          </span>
                          <span className="numero">{conDosDecimales(movimiento.importe)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ) : null}
          </>
        )}
      </Tarjeta>
    </section>
  );
}
